/**
 * Build Queue Types and Management
 *
 * Handles the persistence and management of the build queue for the
 * multi-slide builder. Enables queuing multiple builds and resuming
 * after add-in restarts.
 *
 * Storage Location:
 * CarouselData/carousel_ppt_ondevice_db/build_queue/queue_state.json
 */
let BACKEND_BASE_URL = '';
let authService;
try {
    const shared = require('@carousel-office/shared');
    BACKEND_BASE_URL = shared.PYTHON_BACKEND_URL || "";
    authService = shared.authService;
}
catch { /* shared not available in headless */ }
if (!BACKEND_BASE_URL) {
    console.warn("[BuildQueueManager] PYTHON_BACKEND_URL is not configured.");
}
// ═══════════════════════════════════════════════════════════════════════════
// BUILD QUEUE MANAGER
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Manages the build queue state
 *
 * @example
 * const queueManager = BuildQueueManager.getInstance();
 *
 * // Add a build to the queue
 * await queueManager.enqueue({
 *   planId: 'plan_abc123',
 *   planTitle: 'Q4 Earnings',
 *   conversationId: 'builder_plan_abc123',
 *   documentId: 'deck_xyz789',
 * });
 *
 * // Start the next build
 * const next = await queueManager.startNext();
 *
 * // Get queue state
 * const state = await queueManager.getQueueState();
 */
export class BuildQueueManager {
    constructor() {
        /** In-memory cache of queue state */
        this.queueCache = null;
        /** Whether cache needs refresh */
        this.cacheDirty = true;
    }
    /**
     * Get singleton instance
     */
    static getInstance() {
        if (!BuildQueueManager.instance) {
            BuildQueueManager.instance = new BuildQueueManager();
        }
        return BuildQueueManager.instance;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // QUEUE OPERATIONS
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Get the current queue state
     */
    async getQueueState() {
        if (this.queueCache && !this.cacheDirty) {
            return this.queueCache;
        }
        try {
            const state = await this.loadFromBackend();
            this.queueCache = state;
            this.cacheDirty = false;
            return state;
        }
        catch (error) {
            console.warn('[BuildQueueManager] Failed to load queue state, using empty queue');
            return this.createEmptyState();
        }
    }
    /**
     * Add a build to the queue
     */
    async enqueue(params) {
        const state = await this.getQueueState();
        // Check if already in queue
        const existing = state.queue.find(b => b.planId === params.planId);
        if (existing) {
            console.log(`[BuildQueueManager] Build already in queue: ${params.planId}`);
            return existing;
        }
        // Determine position (after all existing items)
        const position = state.queue.length;
        const newBuild = {
            planId: params.planId,
            planTitle: params.planTitle,
            position,
            status: position === 0 && !state.activePlanId ? 'active' : 'queued',
            enqueuedAt: new Date().toISOString(),
            conversationId: params.conversationId,
            documentId: params.documentId,
            styleGuideDocumentId: params.styleGuideDocumentId,
            progress: params.totalSections && params.totalSlides ? {
                currentSectionIndex: 0,
                totalSections: params.totalSections,
                slidesBuilt: 0,
                totalSlides: params.totalSlides,
            } : undefined,
        };
        // If this is the first build and no active build, make it active
        if (position === 0 && !state.activePlanId) {
            newBuild.status = 'active';
            newBuild.startedAt = new Date().toISOString();
            state.activePlanId = params.planId;
        }
        state.queue.push(newBuild);
        state.lastUpdated = new Date().toISOString();
        await this.saveToBackend(state);
        this.cacheDirty = true;
        console.log(`[BuildQueueManager] Enqueued build: ${params.planId} at position ${position}`);
        return newBuild;
    }
    /**
     * Remove a build from the queue
     */
    async dequeue(planId) {
        const state = await this.getQueueState();
        const index = state.queue.findIndex(b => b.planId === planId);
        if (index === -1) {
            console.log(`[BuildQueueManager] Build not in queue: ${planId}`);
            return false;
        }
        // Remove from queue
        state.queue.splice(index, 1);
        // Update positions
        for (let i = 0; i < state.queue.length; i++) {
            state.queue[i].position = i;
        }
        // Clear active if removed
        if (state.activePlanId === planId) {
            state.activePlanId = undefined;
        }
        state.lastUpdated = new Date().toISOString();
        await this.saveToBackend(state);
        this.cacheDirty = true;
        console.log(`[BuildQueueManager] Dequeued build: ${planId}`);
        return true;
    }
    /**
     * Start the next build in the queue
     * Returns the build that was started, or null if queue is empty
     */
    async startNext() {
        const state = await this.getQueueState();
        // Find the first queued or paused build
        const nextBuild = state.queue.find(b => b.status === 'queued' || b.status === 'paused');
        if (!nextBuild) {
            console.log('[BuildQueueManager] No builds available to start');
            return null;
        }
        // Update the build
        nextBuild.status = 'active';
        nextBuild.startedAt = nextBuild.startedAt || new Date().toISOString();
        nextBuild.pausedAt = undefined;
        nextBuild.position = 0;
        // Update positions for others
        for (const build of state.queue) {
            if (build.planId !== nextBuild.planId && build.position >= 0) {
                build.position = state.queue.indexOf(build);
            }
        }
        state.activePlanId = nextBuild.planId;
        state.lastUpdated = new Date().toISOString();
        await this.saveToBackend(state);
        this.cacheDirty = true;
        console.log(`[BuildQueueManager] Started build: ${nextBuild.planId}`);
        return nextBuild;
    }
    /**
     * Pause the currently active build
     */
    async pauseActive() {
        const state = await this.getQueueState();
        if (!state.activePlanId) {
            console.log('[BuildQueueManager] No active build to pause');
            return null;
        }
        const activeBuild = state.queue.find(b => b.planId === state.activePlanId);
        if (!activeBuild) {
            console.log('[BuildQueueManager] Active build not found in queue');
            return null;
        }
        activeBuild.status = 'paused';
        activeBuild.pausedAt = new Date().toISOString();
        state.activePlanId = undefined;
        state.lastUpdated = new Date().toISOString();
        await this.saveToBackend(state);
        this.cacheDirty = true;
        console.log(`[BuildQueueManager] Paused build: ${activeBuild.planId}`);
        return activeBuild;
    }
    /**
     * Complete a build (success or failure)
     */
    async completeBuild(planId, status, error) {
        const state = await this.getQueueState();
        const build = state.queue.find(b => b.planId === planId);
        if (!build) {
            console.log(`[BuildQueueManager] Build not found: ${planId}`);
            return;
        }
        build.status = status;
        build.completedAt = new Date().toISOString();
        if (error) {
            build.error = error;
        }
        // Clear active if completed
        if (state.activePlanId === planId) {
            state.activePlanId = undefined;
        }
        state.lastUpdated = new Date().toISOString();
        await this.saveToBackend(state);
        this.cacheDirty = true;
        console.log(`[BuildQueueManager] Build ${status}: ${planId}`);
    }
    /**
     * Update build progress
     */
    async updateProgress(planId, progress) {
        const state = await this.getQueueState();
        const build = state.queue.find(b => b.planId === planId);
        if (!build) {
            return;
        }
        build.progress = progress;
        state.lastUpdated = new Date().toISOString();
        await this.saveToBackend(state);
        this.cacheDirty = true;
    }
    /**
     * Reorder the queue
     */
    async reorder(planIds) {
        const state = await this.getQueueState();
        // Build new order
        const newQueue = [];
        for (let i = 0; i < planIds.length; i++) {
            const build = state.queue.find(b => b.planId === planIds[i]);
            if (build) {
                build.position = i;
                newQueue.push(build);
            }
        }
        // Add any builds not in the new order at the end
        for (const build of state.queue) {
            if (!newQueue.includes(build)) {
                build.position = newQueue.length;
                newQueue.push(build);
            }
        }
        state.queue = newQueue;
        state.lastUpdated = new Date().toISOString();
        await this.saveToBackend(state);
        this.cacheDirty = true;
        console.log('[BuildQueueManager] Reordered queue');
    }
    /**
     * Get the currently active build
     */
    async getActiveBuild() {
        const state = await this.getQueueState();
        if (!state.activePlanId) {
            return null;
        }
        return state.queue.find(b => b.planId === state.activePlanId) || null;
    }
    /**
     * Get queued builds (not active)
     */
    async getQueuedBuilds() {
        const state = await this.getQueueState();
        return state.queue.filter(b => b.status === 'queued').sort((a, b) => a.position - b.position);
    }
    /**
     * Check if a build is in the queue
     */
    async isInQueue(planId) {
        const state = await this.getQueueState();
        return state.queue.some(b => b.planId === planId);
    }
    /**
     * Clear completed/failed builds from the queue
     */
    async clearCompleted() {
        const state = await this.getQueueState();
        const before = state.queue.length;
        state.queue = state.queue.filter(b => b.status !== 'completed' && b.status !== 'failed' && b.status !== 'cancelled');
        const removed = before - state.queue.length;
        if (removed > 0) {
            // Reorder positions
            for (let i = 0; i < state.queue.length; i++) {
                state.queue[i].position = i;
            }
            state.lastUpdated = new Date().toISOString();
            await this.saveToBackend(state);
            this.cacheDirty = true;
        }
        return removed;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // BACKEND API
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Load queue state from backend
     */
    async loadFromBackend() {
        const authHeader = authService?.getAuthHeader();
        const response = await fetch(`${BACKEND_BASE_URL}/build/queue`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...(authHeader ? { 'Authorization': authHeader } : {}),
            },
        });
        if (response.status === 404) {
            return this.createEmptyState();
        }
        if (!response.ok) {
            throw new Error(`Failed to load queue state: ${response.statusText}`);
        }
        return response.json();
    }
    /**
     * Save queue state to backend
     */
    async saveToBackend(state) {
        const authHeader = authService?.getAuthHeader();
        const response = await fetch(`${BACKEND_BASE_URL}/build/queue`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...(authHeader ? { 'Authorization': authHeader } : {}),
            },
            body: JSON.stringify(state),
        });
        if (!response.ok) {
            throw new Error(`Failed to save queue state: ${response.statusText}`);
        }
    }
    /**
     * Create an empty queue state
     */
    createEmptyState() {
        return {
            version: '1.0.0',
            queue: [],
            lastUpdated: new Date().toISOString(),
        };
    }
    /**
     * Clear the cache (for testing)
     */
    clearCache() {
        this.queueCache = null;
        this.cacheDirty = true;
    }
}
// Export singleton getter
export const getBuildQueueManager = () => BuildQueueManager.getInstance();
